import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { X, Upload, Apple } from 'lucide-react';
import { StandardIngredient, LanguageCode, IngredientUnit } from '../../types/ingredients';
import type { IngredientNutrition } from '../../types/nutrition';
import { NutritionalFactModal } from './nutrition/NutritionalFactModal';
import {
  getUnitDimension,
  normalizeDensity,
  VOLUME_TO_ML,
} from '../../utils/unitDimension';

interface IngredientFormProps {
  ingredient?: StandardIngredient;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  processImage: (file: File) => Promise<string>;
  /** Manual USDA suggest (edit mode, piece without weight). */
  onSuggestUsda?: () => void;
  suggestingUsda?: boolean;
}

export function IngredientForm({
  ingredient,
  onSubmit,
  onCancel,
  processImage,
  onSuggestUsda,
  suggestingUsda = false,
}: IngredientFormProps) {
  const { t } = useTranslation();
  const attrs = ingredient?.attributes;
  const [language, setLanguage] = useState<LanguageCode>(ingredient?.language || 'en');
  const [imagePreview, setImagePreview] = useState<string | null>(ingredient?.picture || null);
  const [allergens, setAllergens] = useState<string[]>(attrs?.allergens || []);
  const [newAllergen, setNewAllergen] = useState('');
  const [seasonal, setSeasonal] = useState<string[]>(attrs?.seasonal || []);
  const [newSeasonal, setNewSeasonal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameValue, setNameValue] = useState(ingredient?.name || '');
  const [nutrition, setNutrition] = useState<IngredientNutrition | undefined>(
    ingredient?.nutrition
  );
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [preferredUnit, setPreferredUnit] = useState<IngredientUnit>(
    ingredient?.preferredUnit || 'piece'
  );
  const [unitWeightG, setUnitWeightG] = useState(
    ingredient?.unitWeight_g != null ? String(ingredient.unitWeight_g) : ''
  );
  const [densityVolumeAmount, setDensityVolumeAmount] = useState('100');
  const [densityVolumeUnit, setDensityVolumeUnit] = useState('milliliter');
  const [densityWeightG, setDensityWeightG] = useState(
    ingredient?.density_g_per_mL != null
      ? String(Math.round(ingredient.density_g_per_mL * 100 * 10) / 10)
      : ''
  );
  const unitDimension = getUnitDimension(preferredUnit);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': []
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        try {
          const base64Image = await processImage(file);
          setImagePreview(base64Image);
        } catch (error) {
          console.error('Error processing image:', error);
          alert('Error processing image. Please try again.');
        }
      }
    }
  });

  const handleAddAllergen = () => {
    if (newAllergen.trim()) {
      setAllergens([...allergens, newAllergen.trim()]);
      setNewAllergen('');
    }
  };

  const handleRemoveAllergen = (index: number) => {
    setAllergens(allergens.filter((_, i) => i !== index));
  };

  const handleAddSeasonal = () => {
    if (newSeasonal.trim()) {
      setSeasonal([...seasonal, newSeasonal.trim()]);
      setNewSeasonal('');
    }
  };

  const handleRemoveSeasonal = (index: number) => {
    setSeasonal(seasonal.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      
      const dim = getUnitDimension(preferredUnit);
      const data: Record<string, unknown> = {
        name: formData.get('name'),
        language,
        category: formData.get('category'),
        aliases: formData.get('aliases')?.toString().split(',').map(s => s.trim()).filter(Boolean) || [],
        preferredUnit,
        picture: imagePreview || '',
        attributes: {
          glutenFree: formData.get('glutenFree') === 'on',
          vegan: formData.get('vegan') === 'on',
          vegetarian: formData.get('vegetarian') === 'on',
          calories:
            nutrition?.per100g?.kcal != null && !Number.isNaN(nutrition.per100g.kcal)
              ? nutrition.per100g.kcal
              : formData.get('calories')?.toString().trim()
                ? Number(formData.get('calories'))
                : undefined,
          organic: formData.get('organic') === 'on',
          allergens,
          seasonal
        },
        ...(nutrition ? { nutrition } : {}),
      };

      if (dim === 'count' || dim === 'mass') {
        const w = unitWeightG.trim() ? Number(unitWeightG) : null;
        data.unitWeight_g = w != null && Number.isFinite(w) && w > 0 ? w : null;
        if (data.unitWeight_g != null && !ingredient?.unitWeightSource) {
          data.unitWeightSource = 'manual';
          data.unitWeightConfidence = 'reliable';
        } else if (data.unitWeight_g != null && ingredient?.unitWeightSource === 'manual') {
          data.unitWeightSource = 'manual';
          data.unitWeightConfidence = 'reliable';
        } else if (data.unitWeight_g == null) {
          data.unitWeightSource = null;
          data.unitWeightConfidence = null;
        }
      } else if (dim === 'volume') {
        const weight = densityWeightG.trim() ? Number(densityWeightG) : null;
        const vol = densityVolumeAmount.trim() ? Number(densityVolumeAmount) : null;
        if (weight != null && vol != null && weight > 0 && vol > 0) {
          data.density_g_per_mL = normalizeDensity(weight, vol, densityVolumeUnit);
          data.densitySource = 'manual';
        } else if (!densityWeightG.trim()) {
          data.density_g_per_mL = null;
          data.densitySource = null;
        }
      }

      await onSubmit(data);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error saving ingredient. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-display font-bold text-menioo-navy mb-6">
        {ingredient ? 'Edit Ingredient' : 'Add New Ingredient'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Language Selector */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Language
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as LanguageCode)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Name
          </label>
          <input
            type="text"
            name="name"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            required
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Category
          </label>
          <select
            name="category"
            defaultValue={ingredient?.category || 'other'}
            required
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          >
            {Object.keys(t('categories', { returnObjects: true })).map((category) => (
              <option key={category} value={category}>
                {t(`categories.${category}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Aliases */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Aliases (comma-separated)
          </label>
          <input
            type="text"
            name="aliases"
            defaultValue={(ingredient?.aliases || []).join(', ')}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          />
        </div>

        {/* Preferred Unit */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Preferred Unit
          </label>
          <select
            name="preferredUnit"
            value={preferredUnit}
            onChange={(e) => setPreferredUnit(e.target.value as IngredientUnit)}
            required
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
          >
            {Object.keys(t('units', { returnObjects: true })).map((unit) => (
              <option key={unit} value={unit}>
                {t(`units.${unit}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Conversion: unitWeight for solids (even if preferred is gram), density for liquids */}
        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4 space-y-4">
          {(unitDimension === 'count' || unitDimension === 'mass') && (
            <div>
              <label className="block text-sm font-medium text-menioo-navy mb-1">
                Poids moyen d&apos;une pièce (g)
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={unitWeightG}
                  onChange={(e) => setUnitWeightG(e.target.value)}
                  placeholder="120"
                  className="w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
                />
                {onSuggestUsda && !unitWeightG.trim() && (
                  <button
                    type="button"
                    disabled={suggestingUsda}
                    onClick={onSuggestUsda}
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {suggestingUsda ? 'Recherche…' : 'Chercher dans USDA'}
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Utile même si l&apos;unité préférée est le gramme — ex. « 4 amandes » dans une
                recette. 1 amande ≈ 1,2&nbsp;g, 1 carotte ≈ 120&nbsp;g.
              </p>
            </div>
          )}

          {unitDimension === 'volume' && (
            <div>
              <label className="block text-sm font-medium text-menioo-navy mb-1">
                Équivalence volume ↔ poids
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={densityVolumeAmount}
                  onChange={(e) => setDensityVolumeAmount(e.target.value)}
                  className="w-24 rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
                <select
                  value={densityVolumeUnit}
                  onChange={(e) => setDensityVolumeUnit(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  {Object.keys(VOLUME_TO_ML).map((u) => (
                    <option key={u} value={u}>
                      {t(`units.${u}`, { defaultValue: u })}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-500">=</span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={densityWeightG}
                  onChange={(e) => setDensityWeightG(e.target.value)}
                  placeholder="91"
                  className="w-24 rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
                <span className="text-sm text-gray-600">g</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Indique combien pèse ce volume — ex. 100&nbsp;mL d&apos;huile d&apos;olive ≈ 91&nbsp;g.
                Seule la densité normalisée (g/mL) est enregistrée.
              </p>
              {ingredient?.density_g_per_mL != null && (
                <p className="mt-1 text-xs text-menioo-green">
                  Densité actuelle : {ingredient.density_g_per_mL.toFixed(3)} g/mL
                </p>
              )}
            </div>
          )}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-menioo-navy mb-2">
            Image
          </label>
          <div
            {...getRootProps()}
            className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-menioo-green transition-colors cursor-pointer bg-gray-50"
          >
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <input {...getInputProps()} />
                <p>Drag and drop an image, or click to select</p>
              </div>
            </div>
          </div>
          {imagePreview && (
            <div className="mt-4 relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="h-24 w-24 object-cover rounded-md border border-gray-200"
              />
              <button
                type="button"
                onClick={() => setImagePreview(null)}
                className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 hover:bg-red-200 transition-colors"
              >
                <X className="h-4 w-4 text-red-600" />
              </button>
            </div>
          )}
        </div>

        {/* Attributes Section */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-menioo-navy">Attributes</h4>
          
          {/* Checkboxes in 2x2 grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="glutenFree"
                  defaultChecked={attrs?.glutenFree}
                  className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                />
                <span className="text-sm text-gray-700">Gluten Free</span>
              </label>
            </div>
            
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="vegan"
                  defaultChecked={attrs?.vegan}
                  className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                />
                <span className="text-sm text-gray-700">Vegan</span>
              </label>
            </div>
            
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="vegetarian"
                  defaultChecked={attrs?.vegetarian}
                  className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                />
                <span className="text-sm text-gray-700">Vegetarian</span>
              </label>
            </div>
            
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="organic"
                  defaultChecked={attrs?.organic}
                  className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                />
                <span className="text-sm text-gray-700">Organic</span>
              </label>
            </div>
          </div>

          {/* Nutritional fact */}
          <div className="rounded-md border border-gray-200 p-4 space-y-3 bg-gray-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-menioo-navy">Données nutritionnelles</p>
                <p className="text-xs text-gray-500">
                  CIQUAL → Open Food Facts → saisie manuelle. Prépare le futur Nutri-Score.
                </p>
              </div>
              <button
                type="button"
                disabled={!nameValue.trim()}
                onClick={() => setShowNutritionModal(true)}
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-40"
              >
                <Apple className="h-4 w-4 mr-1.5" />
                Nutritional fact
              </button>
            </div>
            {nutrition?.matchedName && (
              <div className="text-xs text-gray-600 space-y-1">
                <p>
                  Source : <strong>{nutrition.source}</strong>
                  {nutrition.matchStatus ? ` (${nutrition.matchStatus})` : ''}
                  {nutrition.manualOverride ? ' — override manuel' : ''}
                </p>
                <p>Match : {nutrition.matchedName}</p>
                <p>
                  {nutrition.per100g?.kcal ?? '—'} kcal · P {nutrition.per100g?.protein_g ?? '—'}g · G{' '}
                  {nutrition.per100g?.carbs_g ?? '—'}g · L {nutrition.per100g?.fat_g ?? '—'}g
                </p>
              </div>
            )}
          </div>

          {/* Calories */}
          <div>
            <label className="block text-sm font-medium text-menioo-navy mb-2">
              Calories (par 100g/100ml) <span className="text-gray-400 text-xs font-normal">(optionnel — sync auto si match)</span>
            </label>
            <input
              type="number"
              name="calories"
              defaultValue={
                nutrition?.per100g?.kcal != null && !Number.isNaN(nutrition.per100g.kcal)
                  ? nutrition.per100g.kcal
                  : attrs?.calories != null && !Number.isNaN(attrs.calories)
                    ? attrs.calories
                    : ''
              }
              key={`cal-${ingredient?.id || 'new'}-${nutrition?.per100g?.kcal ?? attrs?.calories ?? 'empty'}`}
              min="0"
              step="any"
              placeholder="Laisser vide si inconnu"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
            />
          </div>

          {/* Allergens */}
          <div>
            <label className="block text-sm font-medium text-menioo-navy mb-2">
              Allergens
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newAllergen}
                onChange={(e) => setNewAllergen(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
                placeholder="Add allergen"
              />
              <button
                type="button"
                onClick={handleAddAllergen}
                className="px-4 py-2 bg-menioo-green text-white rounded-md hover:bg-menioo-green-dark transition-colors text-sm font-medium"
              >
                Add
              </button>
            </div>
            {allergens.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {allergens.map((allergen, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                  >
                    {allergen}
                    <button
                      type="button"
                      onClick={() => handleRemoveAllergen(index)}
                      className="ml-1 text-red-600 hover:text-red-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Seasonal Availability */}
          <div>
            <label className="block text-sm font-medium text-menioo-navy mb-2">
              Seasonal Availability
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newSeasonal}
                onChange={(e) => setNewSeasonal(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
                placeholder="Add season"
              />
              <button
                type="button"
                onClick={handleAddSeasonal}
                className="px-4 py-2 bg-menioo-green text-white rounded-md hover:bg-menioo-green-dark transition-colors text-sm font-medium"
              >
                Add
              </button>
            </div>
            {seasonal.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {seasonal.map((season, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {season}
                    <button
                      type="button"
                      onClick={() => handleRemoveSeasonal(index)}
                      className="ml-1 text-green-600 hover:text-green-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 text-sm font-medium text-white bg-menioo-green rounded-md hover:bg-menioo-green-dark disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? 'Saving...' : ingredient ? 'Update Ingredient' : 'Add Ingredient'}
          </button>
        </div>
      </form>

      {showNutritionModal && (
        <NutritionalFactModal
          ingredientName={nameValue.trim()}
          ingredientId={ingredient?.id}
          currentNutrition={nutrition}
          onClose={() => setShowNutritionModal(false)}
          onApply={(n) => setNutrition(n)}
        />
      )}
    </div>
  );
}